import type { Ride } from "../ride-center/types/ride";
import type { ActivitySession, ActivitySource } from "./activitySources";
import { matchesAlias, normalizeCsvHeader, parseCsvDiagnostics } from "./csvNormalization";
import { parseTymewearTimeSeries } from "./tymewearTime";
import { buildCoachSummary, type CoachSummary } from "./coachSummary";
import { buildCoachPrioritySummary, type CoachPrioritySummary } from "./coachPriority";

export const UNIFIED_ACTIVITY_ANALYSIS_VERSION = "unified-analysis.v2";

export type PrimaryRideClass = "recovery" | "endurance" | "tempo" | "sweet_spot" | "threshold" | "vo2" | "sprint" | "race" | "group_ride" | "mixed" | "unknown";
export type SourceDomain = "ride" | "respiratory" | "hydration" | "thermal" | "nutrition" | "weather" | "strength" | "athlete_notes" | "other";

export type SourceProvenance = {
  sourceId: string;
  filename: string;
  sourceType: ActivitySource["type"];
  domain: SourceDomain;
  role: ActivitySource["role"] | "supporting_ride";
  usedInCalculations: boolean;
  usageStatus: "used" | "partial" | "excluded" | "provenance only";
  usageReason: string;
  status: ActivitySource["status"];
};

export type RideSourceAgreement = {
  sourceId: string;
  canonicalFilename: string;
  supportingFilename: string;
  startTimeDifferenceSeconds: number | null;
  durationDifferenceSeconds: number | null;
  distanceDifferenceMeters: number | null;
  averagePowerDifferenceWatts: number | null;
  confidencePercent: number;
  status: "verified" | "warning";
  warnings: string[];
};

export type TymewearField =
  | "minuteVentilation"
  | "breathingFrequency"
  | "tidalVolume"
  | "respiratoryRate"
  | "deviceThreshold"
  | "qualityFlag"
  | "heartRate"
  | "rpe"
  | "inspiratoryTime"
  | "expiratoryTime"
  | "respiratoryDutyCycle"
  | "inhaleExhaleRatio"
  | "ventilationThreshold"
  | "vt1"
  | "vt2"
  | "segmentLabel";

export type TymewearRecord = {
  timestamp: string;
  elapsedSeconds: number;
  minuteVentilation: number | null;
  breathingFrequency: number | null;
  tidalVolume: number | null;
  respiratoryRate: number | null;
  deviceThreshold: number | null;
  heartRate: number | null;
  rpe: number | null;
  inspiratoryTime: number | null;
  expiratoryTime: number | null;
  respiratoryDutyCycle: number | null;
  inhaleExhaleRatio: number | null;
  ventilationThreshold: number | null;
  vt1: number | null;
  vt2: number | null;
  qualityFlag: string | null;
  segmentLabel: string | null;
};

export type TymewearAnalysis = {
  sourceId: string;
  filename: string;
  parsedRecords: number;
  firstTimestamp: string | null;
  lastTimestamp: string | null;
  overlapPercent: number;
  offsetAppliedSeconds: number;
  alignmentMethod: "absolute_timestamps" | "elapsed_time_to_garmin_start" | "unavailable";
  sampleIntervalSeconds: number | null;
  durationSeconds: number | null;
  alignmentConfidence: "high" | "medium" | "low";
  availableFields: TymewearField[];
  dataCoveragePercent: number;
  aligned: boolean;
  records: TymewearRecord[];
  metrics: Partial<Record<Exclude<TymewearField, "qualityFlag">, {
    average: number;
    maximum: number;
    firstHalfAverage: number;
    secondHalfAverage: number;
    driftPercent: number | null;
  }>>;
  thresholdDistribution: Array<{ label: string; seconds: number; percent: number }>;
  timeAboveVT1Seconds: number | null;
  timeAboveVT2Seconds: number | null;
  ventilationToPowerCorrelation: number | null;
  interpretation: string;
  confidencePercent: number;
  warnings: string[];
  rejectedReason: string | null;
};

export type ActivityDataCoverage = {
  categories: Array<{ domain: SourceDomain; present: boolean; usable: boolean }>;
  activityCompletenessPercent: number;
};

export type UnifiedActivityAnalysisInput = {
  activitySessionId: string;
  canonicalPrimaryRide: Ride;
  primaryThermalDetected: boolean;
  supportingRideSources: Array<{ source: ActivitySource; ride: Ride; agreement: RideSourceAgreement }>;
  companionTimeSeriesSources: TymewearAnalysis[];
  screenshots: ActivitySource[];
  athleteNotes: ActivitySource[];
  weatherData: ActivitySource[];
  nutritionData: ActivitySource[];
  strengthData: ActivitySource[];
  hydrationSources: ActivitySource[];
  thermalSources: ActivitySource[];
  sourceProvenance: SourceProvenance[];
  dataCoverage: ActivityDataCoverage;
  ftpWatts: number;
  warnings: string[];
};

export type RideClassificationAnalysis = {
  type: PrimaryRideClass;
  displayName: string;
  confidencePercent: number;
  primaryStimulus: string;
  secondaryStimulus: string;
  executionStyle: string;
  reason: string;
  showStructuredIntervals: boolean;
};

export type EnduranceExecutionAnalysis = {
  observational: true;
  timeInEnduranceRangeSeconds: number | null;
  normalizedPower: number | null;
  intensityFactor: number | null;
  variabilityIndex: number | null;
  aerobicDecouplingPercent: number | null;
  firstHalfPower: number | null;
  secondHalfPower: number | null;
  firstHalfHeartRate: number | null;
  secondHalfHeartRate: number | null;
  cadenceStabilityPercent: number | null;
  coastingTimeSeconds: number;
  stopTimeSeconds: number;
  climbingContributionPercent: number | null;
  durability: Array<{ afterMinutes: 60 | 90 | 120; powerChangePercent: number | null }>;
  confidencePercent: number;
  warnings: string[];
};

export type UnifiedActivityAnalysisResult = {
  analysisVersion: string;
  input: UnifiedActivityAnalysisInput;
  rideClassification: RideClassificationAnalysis;
  enduranceExecution: EnduranceExecutionAnalysis | null;
  respiratoryAnalysis: TymewearAnalysis | null;
  coachSummary: CoachSummary;
  coachPriority: CoachPrioritySummary;
  rideSourceAgreement: RideSourceAgreement[];
  activityCompletenessPercent: number;
  analysisConfidence: {
    overallPercent: number;
    rideClassificationPercent: number;
    enduranceExecutionPercent: number | null;
    respiratoryAnalysisPercent: number | null;
    hydrationAnalysisPercent: number | null;
  };
  sessionOverview: {
    sessionType: string;
    execution: string;
    primaryAdaptation: string;
    secondaryAdaptation: string;
    respiratoryData: string;
    hydrationData: string;
    overallAnalysisConfidencePercent: number;
  };
  warnings: string[];
};

export type FusionDependencies = {
  parseSupportingRide: (source: ActivitySource) => Promise<Ride>;
};

export type RunUnifiedActivityAnalysisOptions = {
  generatedAt?: string;
};

const TYMEWEAR_ALIASES: Record<TymewearField | "timestamp" | "elapsedSeconds", string[]> = {
  timestamp: ["timestamp", "datetime", "date time"],
  elapsedSeconds: ["elapsed time", "seconds", "sample time"],
  minuteVentilation: ["minute ventilation", "minute ventilation l min", "ventilation", "ve", "ve l min", "ve l/min", "ventilation l/min"],
  breathingFrequency: ["breathing frequency", "breathing rate", "breath frequency", "respiratory rate", "respiration rate", "br", "bf", "rr", "rf", "breaths per minute", "breaths/min", "rpm"],
  tidalVolume: ["tidal volume", "vt", "tv", "tidal volume ml", "tidal volume l"],
  respiratoryRate: ["respiratory rate", "respiration rate", "rr"],
  deviceThreshold: ["device threshold", "threshold", "zone", "zone label", "zone name"],
  heartRate: ["heart rate", "hr", "heartrate"],
  rpe: ["rpe", "rate of perceived exertion"],
  inspiratoryTime: ["inspiratory time"],
  expiratoryTime: ["expiratory time"],
  respiratoryDutyCycle: ["respiratory duty cycle"],
  inhaleExhaleRatio: ["inhale exhale ratio", "i e ratio", "ie ratio"],
  ventilationThreshold: ["ventilation threshold", "respiratory threshold", "vo2 threshold"],
  vt1: ["vt1"],
  vt2: ["vt2"],
  qualityFlag: ["quality flag", "quality", "validity", "status", "signal quality"],
  segmentLabel: ["segment", "segment label", "lap", "block"]
};

function finite(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

function average(values: Array<number | null | undefined>): number | null {
  const valid = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  return valid.length ? valid.reduce((sum, value) => sum + value, 0) / valid.length : null;
}

function percentDifference(first: number | null, second: number | null): number | null {
  if (first === null || second === null || first === 0) return null;
  return ((second - first) / first) * 100;
}

function toTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  if (/^\d+(\.\d+)?$/.test(value.trim())) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function findColumnIndex(headers: string[], aliases: string[]): number {
  return headers.findIndex(header => aliases.some(alias => matchesAlias(header, alias)));
}

function correlation(pairs: Array<[number, number]>): number | null {
  if (pairs.length < 3) return null;
  const xs = pairs.map(pair => pair[0]);
  const ys = pairs.map(pair => pair[1]);
  const xMean = xs.reduce((sum, value) => sum + value, 0) / xs.length;
  const yMean = ys.reduce((sum, value) => sum + value, 0) / ys.length;
  let numerator = 0;
  let xVariance = 0;
  let yVariance = 0;
  for (let index = 0; index < pairs.length; index += 1) {
    const xDiff = xs[index] - xMean;
    const yDiff = ys[index] - yMean;
    numerator += xDiff * yDiff;
    xVariance += xDiff ** 2;
    yVariance += yDiff ** 2;
  }
  if (xVariance === 0 || yVariance === 0) return null;
  return numerator / Math.sqrt(xVariance * yVariance);
}

function sourceDomain(source: ActivitySource): SourceDomain {
  if (/Tymewear/.test(source.type)) return "respiratory";
  if (/Nix/.test(source.type)) return "hydration";
  if (/CORE/.test(source.type)) return "thermal";
  if (/Nutrition|MacroFactor Nutrition/.test(source.type)) return "nutrition";
  if (/Weather/.test(source.type)) return "weather";
  if (/Workout/.test(source.type)) return "strength";
  if (/Athlete Notes/.test(source.type)) return "athlete_notes";
  if (/FIT|GPX|TCX/.test(source.type)) return "ride";
  return "other";
}

function hasPrimaryThermalSignals(ride: Ride): boolean {
  return ride.records.some(record => record.coreTemperatureCelsius !== null && record.coreTemperatureCelsius !== undefined)
    || ride.records.some(record => record.heatStrainIndex !== null && record.heatStrainIndex !== undefined)
    || ride.heatStrainIndexValues.length > 0;
}

function compareRideSources(canonicalSource: ActivitySource, canonical: Ride, source: ActivitySource, supporting: Ride): RideSourceAgreement {
  const canonicalStart = canonical.rideDate ? new Date(canonical.rideDate).getTime() : null;
  const supportingStart = supporting.rideDate ? new Date(supporting.rideDate).getTime() : null;
  const startTimeDifferenceSeconds = canonicalStart === null || supportingStart === null ? null : Math.abs(canonicalStart - supportingStart) / 1000;
  const durationDifferenceSeconds = canonical.elapsedTime === null || supporting.elapsedTime === null ? null : Math.abs(canonical.elapsedTime - supporting.elapsedTime);
  const distanceDifferenceMeters = canonical.distance === null || supporting.distance === null ? null : Math.abs(canonical.distance - supporting.distance);
  const averagePowerDifferenceWatts = canonical.averagePower === null || supporting.averagePower === null ? null : Math.abs(canonical.averagePower - supporting.averagePower);
  const checks = [
    startTimeDifferenceSeconds === null ? null : startTimeDifferenceSeconds <= 600,
    durationDifferenceSeconds === null ? null : durationDifferenceSeconds <= Math.max(120, (canonical.elapsedTime ?? 0) * 0.05),
    distanceDifferenceMeters === null ? null : distanceDifferenceMeters <= Math.max(500, (canonical.distance ?? 0) * 0.05),
    averagePowerDifferenceWatts === null ? null : averagePowerDifferenceWatts <= Math.max(10, (canonical.averagePower ?? 0) * 0.08)
  ].filter((check): check is boolean => check !== null);
  const confidencePercent = checks.length ? Math.round(checks.filter(Boolean).length / checks.length * 100) : 0;
  const warnings: string[] = [];
  if (startTimeDifferenceSeconds !== null && startTimeDifferenceSeconds > 600) warnings.push("Start times differ by more than 10 minutes.");
  if (durationDifferenceSeconds !== null && durationDifferenceSeconds > Math.max(120, (canonical.elapsedTime ?? 0) * 0.05)) warnings.push("Durations differ materially.");
  if (distanceDifferenceMeters !== null && distanceDifferenceMeters > Math.max(500, (canonical.distance ?? 0) * 0.05)) warnings.push("Distances differ materially.");
  if (averagePowerDifferenceWatts !== null && averagePowerDifferenceWatts > Math.max(10, (canonical.averagePower ?? 0) * 0.08)) warnings.push("Average power differs materially.");
  return {
    sourceId: source.id,
    canonicalFilename: canonicalSource.file.name,
    supportingFilename: source.file.name,
    startTimeDifferenceSeconds,
    durationDifferenceSeconds,
    distanceDifferenceMeters,
    averagePowerDifferenceWatts,
    confidencePercent,
    status: warnings.length ? "warning" : "verified",
    warnings
  };
}

export async function analyzeTymewearSource(source: ActivitySource, canonicalRide: Ride): Promise<TymewearAnalysis> {
  const csv = parseCsvDiagnostics(await source.file.text());
  const normalizedHeaders = csv.normalizedHeaders.map(normalizeCsvHeader);
  const fieldIndexes = new Map<string, number>();
  for (const [field, aliases] of Object.entries(TYMEWEAR_ALIASES)) {
    const index = normalizedHeaders.findIndex(header => aliases.some(alias => matchesAlias(header, alias)));
    if (index >= 0) fieldIndexes.set(field, index);
  }
  const timestampFieldIndex = fieldIndexes.get("timestamp");
  const timeHeaderIndex = normalizedHeaders.findIndex(header => header === "time");
  const elapsedSecondsIndex = fieldIndexes.get("elapsedSeconds");
  const timeColumnIndex = timestampFieldIndex ?? elapsedSecondsIndex ?? (timeHeaderIndex >= 0 ? timeHeaderIndex : undefined);
  const rideStart = canonicalRide.rideDate ? new Date(canonicalRide.rideDate).getTime() : null;
  const rideDurationSeconds = canonicalRide.elapsedTime ?? canonicalRide.movingTime ?? 0;
  const rideEnd = rideStart === null ? null : rideStart + rideDurationSeconds * 1000;
  const availableFields = (["minuteVentilation", "breathingFrequency", "tidalVolume", "respiratoryRate", "deviceThreshold", "qualityFlag", "heartRate", "rpe", "inspiratoryTime", "expiratoryTime", "respiratoryDutyCycle", "inhaleExhaleRatio", "ventilationThreshold", "vt1", "vt2", "segmentLabel"] as TymewearField[])
    .filter(field => fieldIndexes.has(field));
  const rawTimeValues = timeColumnIndex === undefined
    ? []
    : csv.rows.map(row => row[timeColumnIndex] || null);
  const parsedTimeSeries = parseTymewearTimeSeries(rawTimeValues);
  const alignmentMethod: TymewearAnalysis["alignmentMethod"] = parsedTimeSeries.points.some(point => point.absoluteTimestampMs !== null)
    ? "absolute_timestamps"
    : parsedTimeSeries.firstValidElapsedSeconds !== null && rideStart !== null
      ? "elapsed_time_to_garmin_start"
      : "unavailable";

  let invalidRowCount = 0;
  const parsed = csv.rows.map((row, index) => {
    const timePoint = parsedTimeSeries.points[index];
    const alignedTimestampMs = timePoint?.absoluteTimestampMs !== null && timePoint?.absoluteTimestampMs !== undefined
      ? timePoint.absoluteTimestampMs
      : (rideStart !== null && timePoint?.elapsedSeconds !== null && parsedTimeSeries.firstValidElapsedSeconds !== null
        ? rideStart + (timePoint.elapsedSeconds - parsedTimeSeries.firstValidElapsedSeconds) * 1000
        : null);
    if (alignedTimestampMs === null || rideStart === null) return null;
    let invalidNumericRow = false;
    const numeric = (field: Exclude<TymewearField, "qualityFlag">) => {
      const index = fieldIndexes.get(field);
      const value = index === undefined ? null : finite(row[index]);
      if (field === "minuteVentilation" || field === "breathingFrequency" || field === "tidalVolume" || field === "respiratoryRate") {
        if (value !== null && value < 0) {
          invalidNumericRow = true;
          return null;
        }
      }
      return value;
    };
    const record = {
      timestamp: new Date(alignedTimestampMs).toISOString(),
      elapsedSeconds: (alignedTimestampMs - rideStart) / 1000,
      minuteVentilation: numeric("minuteVentilation"),
      breathingFrequency: numeric("breathingFrequency"),
      tidalVolume: numeric("tidalVolume"),
      respiratoryRate: numeric("respiratoryRate"),
      deviceThreshold: numeric("deviceThreshold"),
      heartRate: numeric("heartRate"),
      rpe: numeric("rpe"),
      inspiratoryTime: numeric("inspiratoryTime"),
      expiratoryTime: numeric("expiratoryTime"),
      respiratoryDutyCycle: numeric("respiratoryDutyCycle"),
      inhaleExhaleRatio: numeric("inhaleExhaleRatio"),
      ventilationThreshold: numeric("ventilationThreshold"),
      vt1: numeric("vt1"),
      vt2: numeric("vt2"),
      qualityFlag: fieldIndexes.has("qualityFlag") ? row[fieldIndexes.get("qualityFlag")!] || null : null,
      segmentLabel: fieldIndexes.has("segmentLabel") ? row[fieldIndexes.get("segmentLabel")!] || null : null
    } satisfies TymewearRecord;
    if (invalidNumericRow) {
      invalidRowCount += 1;
      return null;
    }
    return record;
  }).filter((record): record is TymewearRecord => record !== null).sort((left, right) => left.elapsedSeconds - right.elapsedSeconds);
  const alignedRecords = parsed.filter(record => record.elapsedSeconds >= 0 && record.elapsedSeconds <= rideDurationSeconds);
  const firstTimestamp = parsed[0]?.timestamp ?? null;
  const lastTimestamp = parsed.at(-1)?.timestamp ?? null;
  const sourceStart = firstTimestamp ? Date.parse(firstTimestamp) : null;
  const sourceEnd = lastTimestamp ? Date.parse(lastTimestamp) : null;
  const overlapMs = rideStart === null || rideEnd === null || sourceStart === null || sourceEnd === null
    ? 0
    : Math.max(0, Math.min(rideEnd, sourceEnd) - Math.max(rideStart, sourceStart));
  const overlapPercent = rideDurationSeconds > 0 ? Math.round(overlapMs / (rideDurationSeconds * 1000) * 100) : 0;
  const durationSeconds = firstTimestamp && lastTimestamp ? Math.max(0, Math.round((Date.parse(lastTimestamp) - Date.parse(firstTimestamp)) / 1000)) : null;
  const sampleIntervalSeconds = parsedTimeSeries.sampleIntervalSeconds;
  const alignmentConfidence: TymewearAnalysis["alignmentConfidence"] = parsedTimeSeries.parseConfidence === "high" && overlapPercent >= 80
    ? "high"
    : parsedTimeSeries.parseConfidence === "low" || overlapPercent < 30
      ? "low"
      : "medium";
  const metrics: TymewearAnalysis["metrics"] = {};
  for (const field of availableFields.filter((candidate): candidate is Exclude<TymewearField, "qualityFlag" | "segmentLabel"> => candidate !== "qualityFlag" && candidate !== "segmentLabel")) {
    const values = alignedRecords.map(record => record[field]);
    const midpoint = rideDurationSeconds / 2;
    const firstHalfAverage = average(alignedRecords.filter(record => record.elapsedSeconds <= midpoint).map(record => record[field]));
    const secondHalfAverage = average(alignedRecords.filter(record => record.elapsedSeconds > midpoint).map(record => record[field]));
    const fieldAverage = average(values);
    const valid = values.filter((value): value is number => value !== null);
    if (fieldAverage !== null && valid.length) {
      const firstHalf = firstHalfAverage ?? fieldAverage;
      const secondHalf = secondHalfAverage ?? fieldAverage;
      metrics[field] = {
        average: fieldAverage,
        maximum: Math.max(...valid),
        firstHalfAverage: firstHalf,
        secondHalfAverage: secondHalf,
        driftPercent: firstHalfAverage !== null && secondHalfAverage !== null ? percentDifference(firstHalfAverage, secondHalfAverage) : null
      };
    }
  }

  const thresholdLabels = ["vt1", "vt2", "deviceThreshold", "ventilationThreshold"] as const;
  const thresholdDistribution = thresholdLabels.map(label => {
    const seconds = alignedRecords.filter(record => {
      const value = record[label];
      return value !== null && value > 0;
    }).length * (canonicalRide.recordingIntervalSeconds ?? 1);
    const percent = rideDurationSeconds > 0 ? Math.round((seconds / rideDurationSeconds) * 100) : 0;
    return { label: label.toUpperCase(), seconds, percent };
  }).filter(item => item.seconds > 0);

  const timeAboveVT1Seconds = metrics.vt1?.average !== undefined
    ? alignedRecords.filter(record => record.minuteVentilation !== null && record.vt1 !== null && record.minuteVentilation > record.vt1).length * (canonicalRide.recordingIntervalSeconds ?? 1)
    : null;
  const timeAboveVT2Seconds = metrics.vt2?.average !== undefined
    ? alignedRecords.filter(record => record.minuteVentilation !== null && record.vt2 !== null && record.minuteVentilation > record.vt2).length * (canonicalRide.recordingIntervalSeconds ?? 1)
    : null;

  const ventilationToPowerPairs: Array<[number, number]> = alignedRecords
    .map(record => {
      const rideRecord = canonicalRide.records.find(candidate => (candidate.elapsedSeconds ?? 0) >= record.elapsedSeconds);
      if (!rideRecord || record.minuteVentilation === null || rideRecord.powerWatts == null) return null;
      return [rideRecord.powerWatts, record.minuteVentilation] as [number, number];
    })
    .filter((pair): pair is [number, number] => pair !== null);
  const ventilationToPowerCorrelation = correlation(ventilationToPowerPairs);

  const warnings: string[] = [];
  if (parsedTimeSeries.stats.validValueCount === 0) warnings.push("No timestamp or elapsed-time column was detected.");
  if (overlapPercent < 50) warnings.push("Tymewear overlap is insufficient for session-level conclusions.");
  if (!availableFields.some(field => field !== "qualityFlag")) warnings.push("No supported respiratory fields were detected.");
  if (alignmentMethod === "elapsed_time_to_garmin_start") warnings.push("Aligned Tymewear elapsed-time samples to primary ride start.");
  if (invalidRowCount > 0) warnings.push(`Skipped ${invalidRowCount} rows with invalid respiratory values.`);
  if (alignedRecords.length < parsed.length) warnings.push(`Skipped ${parsed.length - alignedRecords.length} rows outside ride window or with invalid values.`);
  const aligned = overlapPercent >= 50 && alignedRecords.length > 0;
  const hasCoreRespiratoryMetrics = Boolean(metrics.minuteVentilation || metrics.breathingFrequency || metrics.tidalVolume);
  let rejectedReason: string | null = null;
  if (!hasCoreRespiratoryMetrics) {
    rejectedReason = "No usable minute-ventilation, breathing-frequency, or tidal-volume metrics were produced.";
  } else if (!aligned) {
    rejectedReason = overlapPercent < 50
      ? `Overlap was ${overlapPercent}% which is below the 50% aligned-confidence threshold.`
      : "No aligned records were available in the primary ride window.";
  }
  const interpretation = aligned
    ? "Respiratory data aligned to the ride timeline with sufficient overlap."
    : hasCoreRespiratoryMetrics
      ? "Respiratory source parsed with partial alignment; metrics are available with reduced confidence."
      : "Respiratory source parsed, but alignment or field coverage limits confidence.";
  return {
    sourceId: source.id,
    filename: source.file.name,
    parsedRecords: parsed.length,
    firstTimestamp,
    lastTimestamp,
    overlapPercent,
    offsetAppliedSeconds: 0,
    alignmentMethod,
    sampleIntervalSeconds,
    durationSeconds,
    alignmentConfidence,
    availableFields,
    dataCoveragePercent: overlapPercent,
    aligned,
    records: alignedRecords,
    metrics,
    thresholdDistribution,
    timeAboveVT1Seconds,
    timeAboveVT2Seconds,
    ventilationToPowerCorrelation,
    interpretation,
    confidencePercent: aligned ? Math.min(95, 65 + Math.round(overlapPercent * 0.3)) : Math.min(45, overlapPercent),
    warnings,
    rejectedReason
  };
}

function dataCoverage(session: ActivitySession): ActivityDataCoverage {
  const sources = [session.primary, ...session.supportingRideSources, ...session.companionSources].filter((source): source is ActivitySource => source !== null);
  const domains: SourceDomain[] = ["ride", "respiratory", "hydration", "thermal", "nutrition", "weather", "strength", "athlete_notes"];
  const categories = domains.map(domain => {
    const matching = sources.filter(source => sourceDomain(source) === domain);
    return { domain, present: matching.length > 0, usable: matching.some(source => source.status === "parsed" || source.status === "ready") };
  });
  return { categories, activityCompletenessPercent: Math.round(categories.filter(category => category.present).length / categories.length * 100) };
}

export async function buildUnifiedActivityAnalysisInput(
  session: ActivitySession,
  canonicalRide: Ride,
  ftpWatts: number,
  dependencies: FusionDependencies
): Promise<UnifiedActivityAnalysisInput> {
  if (!session.primary) throw new Error("A canonical primary ride source is required.");
  const warnings: string[] = [];
  const supportingRideSources = await Promise.all(session.supportingRideSources.map(async source => {
    const ride = await dependencies.parseSupportingRide(source);
    const agreement = compareRideSources(session.primary!, canonicalRide, source, ride);
    warnings.push(...agreement.warnings.map(warning => `${source.file.name}: ${warning}`));
    return { source, ride, agreement };
  }));
  const tymewearSources = session.companionSources.filter(source => source.type === "Tymewear CSV");
  const companionTimeSeriesSources = await Promise.all(tymewearSources.map(source => analyzeTymewearSource(source, canonicalRide)));
  warnings.push(...companionTimeSeriesSources.flatMap(source => source.warnings.map(warning => `${source.filename}: ${warning}`)));
  const allSources = [session.primary, ...session.supportingRideSources, ...session.companionSources];
  const tymewearBySourceId = new Map(companionTimeSeriesSources.map(source => [source.sourceId, source]));
  const provenance = allSources.map(source => {
    const domain = sourceDomain(source);
    const role = session.supportingRideSources.some(candidate => candidate.id === source.id) ? "supporting_ride" as const : source.role;
    let usageStatus: SourceProvenance["usageStatus"] = "provenance only";
    let usageReason = "Source retained for provenance.";

    if (source.id === session.primary!.id) {
      usageStatus = "used";
      usageReason = "Canonical primary ride used in all ride calculations.";
    } else if (role === "supporting_ride") {
      usageStatus = "provenance only";
      usageReason = "Supporting ride retained for source agreement verification.";
    } else if (source.type === "Tymewear CSV") {
      const analysis = tymewearBySourceId.get(source.id);
      const hasMetrics = Boolean(analysis?.metrics.minuteVentilation || analysis?.metrics.breathingFrequency || analysis?.metrics.tidalVolume);
      if (analysis?.aligned && hasMetrics) {
        usageStatus = "used";
        usageReason = "Aligned respiratory metrics included in respiratory analysis.";
      } else if (analysis && hasMetrics) {
        usageStatus = "partial";
        usageReason = analysis.rejectedReason ?? "Respiratory metrics available with limited alignment confidence.";
      } else {
        usageStatus = "excluded";
        usageReason = analysis?.rejectedReason ?? "No usable respiratory metrics were produced.";
      }
    } else if (domain === "hydration" || domain === "thermal" || domain === "nutrition" || domain === "weather" || domain === "strength" || domain === "athlete_notes") {
      usageStatus = "provenance only";
      usageReason = "Source retained for future domain-specific analysis.";
    }

    return {
      sourceId: source.id,
      filename: source.file.name,
      sourceType: source.type,
      domain,
      role,
      usedInCalculations: usageStatus === "used",
      usageStatus,
      usageReason,
      status: source.status
    };
  });
  const byDomain = (domain: SourceDomain) => session.companionSources.filter(source => sourceDomain(source) === domain);
  return {
    activitySessionId: session.id,
    canonicalPrimaryRide: canonicalRide,
    primaryThermalDetected: hasPrimaryThermalSignals(canonicalRide),
    supportingRideSources,
    companionTimeSeriesSources,
    screenshots: session.companionSources.filter(source => source.role === "screenshot"),
    athleteNotes: byDomain("athlete_notes"),
    weatherData: byDomain("weather"),
    nutritionData: byDomain("nutrition"),
    strengthData: byDomain("strength"),
    hydrationSources: byDomain("hydration"),
    thermalSources: byDomain("thermal"),
    sourceProvenance: provenance,
    dataCoverage: dataCoverage(session),
    ftpWatts,
    warnings
  };
}

function classifyRide(input: UnifiedActivityAnalysisInput): RideClassificationAnalysis {
  const analytics = input.canonicalPrimaryRide.rideAnalytics;
  const workout = analytics?.workoutClassification;
  const structured = !!workout && workout.structuredSetCount > 0 && workout.detectedSets.some(set => set.repetitionCount >= 2 && set.confidence >= 0.55);
  const hasPlannedWorkout = input.strengthData.length > 0;
  const showStructuredIntervals = structured || hasPlannedWorkout;
  const zonePercent = (name: string) => analytics?.powerZones.find(zone => zone.zoneName === name)?.percentageOfMovingTime ?? 0;
  const lowerZonePercent = zonePercent("Zone 1") + zonePercent("Zone 2");
  const intensityFactor = analytics?.intensityFactor ?? input.canonicalPrimaryRide.if ?? null;
  const variability = analytics?.variabilityIndex ?? input.canonicalPrimaryRide.vi ?? null;
  if (!showStructuredIntervals && intensityFactor !== null && intensityFactor >= 0.6 && intensityFactor <= 0.76 && lowerZonePercent >= 60 && (variability === null || variability <= 1.12)) {
    const confidence = Math.round(Math.min(97, 82 + Math.max(0, lowerZonePercent - 60) * 0.25 + (variability !== null && variability <= 1.08 ? 5 : 0)));
    return {
      type: "endurance",
      displayName: "Endurance Ride",
      confidencePercent: confidence,
      primaryStimulus: "Aerobic endurance",
      secondaryStimulus: input.canonicalPrimaryRide.movingTime && input.canonicalPrimaryRide.movingTime >= 5400 ? "Durability" : "Muscular endurance",
      executionStyle: variability !== null && variability <= 1.08 ? "Steady aerobic workload with low variability" : "Variable aerobic workload",
      reason: `Most valid power time (${Math.round(lowerZonePercent)}%) was spent in Z1/Z2, intensity factor was ${intensityFactor.toFixed(2)}, and no coherent repeated interval set was detected.`,
      showStructuredIntervals
    };
  }
  const workoutType = workout?.workoutType;
  const typeMap: Partial<Record<NonNullable<typeof workoutType>, PrimaryRideClass>> = { recovery: "recovery", endurance: "endurance", tempo: "tempo", "sweet-spot": "sweet_spot", threshold: "threshold", vo2max: "vo2", sprint: "sprint", mixed: "mixed" };
  const type = workoutType ? typeMap[workoutType] ?? "unknown" : "unknown";
  const displayName = workout?.workoutName && workout.workoutName !== "Unclassified Structured Workout" ? workout.workoutName : type === "unknown" ? "Unknown Ride" : `${type.replace("_", " ")} Ride`;
  return {
    type,
    displayName,
    confidencePercent: Math.round((workout?.confidence ?? 0.45) * 100),
    primaryStimulus: type === "threshold" ? "Lactate threshold" : type === "tempo" || type === "sweet_spot" ? "Aerobic power" : type === "recovery" ? "Recovery" : "Mixed training stimulus",
    secondaryStimulus: type === "threshold" ? "Muscular endurance" : "Aerobic endurance",
    executionStyle: showStructuredIntervals ? "Structured interval execution" : (workout?.classificationCategory === "free-ride" ? "Free-ride execution" : "Continuous ride execution"),
    reason: workout?.explanation ?? workout?.summary ?? "Available evidence did not support a more specific ride class.",
    showStructuredIntervals
  };
}

function coefficientStability(values: number[]): number | null {
  if (values.length < 2) return null;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  if (!mean) return null;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.max(0, Math.round((1 - Math.sqrt(variance) / mean) * 100));
}

function enduranceExecution(input: UnifiedActivityAnalysisInput, classification: RideClassificationAnalysis): EnduranceExecutionAnalysis | null {
  if (!["recovery", "endurance", "group_ride", "mixed"].includes(classification.type)) return null;
  const ride = input.canonicalPrimaryRide;
  const analytics = ride.rideAnalytics;
  const powers = ride.records.map(record => finite(record.powerWatts));
  const hrs = ride.records.map(record => finite(record.heartRateBpm));
  const midpoint = Math.floor(ride.records.length / 2);
  const cadences = ride.records.map(record => finite(record.cadenceRpm)).filter((value): value is number => value !== null && value > 0);
  const enduranceZone = analytics?.powerZones.find(zone => zone.zoneName === "Zone 2");
  const coastingRecords = powers.filter(power => power !== null && power <= 5).length;
  const sampleSeconds = ride.recordingIntervalSeconds ?? 1;
  const durability: EnduranceExecutionAnalysis["durability"] = ([60, 90, 120] as const).map(afterMinutes => {
    const elapsedTarget = afterMinutes * 60;
    if ((ride.movingTime ?? 0) < elapsedTarget + 600) return { afterMinutes, powerChangePercent: null };
    const before = average(ride.records.filter(record => (record.elapsedSeconds ?? 0) < elapsedTarget).map(record => record.powerWatts));
    const after = average(ride.records.filter(record => (record.elapsedSeconds ?? 0) >= elapsedTarget).map(record => record.powerWatts));
    return { afterMinutes, powerChangePercent: percentDifference(before, after) };
  });
  let climbingSeconds = 0;
  for (let index = 1; index < ride.records.length; index += 1) {
    const previous = ride.records[index - 1].altitudeMeters;
    const current = ride.records[index].altitudeMeters;
    if (previous != null && current != null && current > previous) climbingSeconds += sampleSeconds;
  }
  const warnings: string[] = ["Observational analysis using athlete profile zones; no prescribed target was attached."];
  if (!hrs.some(value => value !== null)) warnings.push("Heart-rate context was unavailable.");
  return {
    observational: true,
    timeInEnduranceRangeSeconds: enduranceZone?.seconds ?? null,
    normalizedPower: analytics?.normalizedPower ?? ride.normalizedPower ?? null,
    intensityFactor: analytics?.intensityFactor ?? ride.if ?? null,
    variabilityIndex: analytics?.variabilityIndex ?? ride.vi ?? null,
    aerobicDecouplingPercent: analytics?.aerobicDecoupling.decouplingPercent ?? null,
    firstHalfPower: average(powers.slice(0, midpoint)),
    secondHalfPower: average(powers.slice(midpoint)),
    firstHalfHeartRate: average(hrs.slice(0, midpoint)),
    secondHalfHeartRate: average(hrs.slice(midpoint)),
    cadenceStabilityPercent: coefficientStability(cadences),
    coastingTimeSeconds: coastingRecords * sampleSeconds,
    stopTimeSeconds: Math.max(0, (ride.elapsedTime ?? 0) - (ride.movingTime ?? ride.elapsedTime ?? 0)),
    climbingContributionPercent: ride.movingTime ? Math.round(climbingSeconds / ride.movingTime * 100) : null,
    durability,
    confidencePercent: Math.min(96, classification.confidencePercent + (hrs.some(value => value !== null) ? 2 : 0)),
    warnings
  };
}

export function runUnifiedActivityAnalysis(
  input: UnifiedActivityAnalysisInput,
  options: RunUnifiedActivityAnalysisOptions = {}
): UnifiedActivityAnalysisResult {
  const rideClassification = classifyRide(input);
  const endurance = enduranceExecution(input, rideClassification);
  const hasUsableRespiratoryMetrics = (source: TymewearAnalysis) => Boolean(source.metrics.minuteVentilation || source.metrics.breathingFrequency || source.metrics.tidalVolume);
  const respiratory = input.companionTimeSeriesSources
    .filter(source => hasUsableRespiratoryMetrics(source))
    .sort((left, right) => {
      const leftScore = (left.aligned ? 1 : 0) * 1000 + left.overlapPercent + left.parsedRecords;
      const rightScore = (right.aligned ? 1 : 0) * 1000 + right.overlapPercent + right.parsedRecords;
      return rightScore - leftScore;
    })[0] ?? null;
  const confidenceValues = [rideClassification.confidencePercent, endurance?.confidencePercent, respiratory?.confidencePercent].filter((value): value is number => value !== null && value !== undefined);
  const overallPercent = Math.round(confidenceValues.reduce((sum, value) => sum + value, 0) / confidenceValues.length);
  const hydrationPresent = input.hydrationSources.length > 0;
  const generatedAt = options.generatedAt ?? new Date().toISOString();
  const coachSummary = buildCoachSummary({
    input,
    rideClassification,
    enduranceExecution: endurance,
    respiratoryAnalysis: respiratory,
    analysisConfidencePercent: overallPercent,
    generatedAt
  });
  const coachPriority = buildCoachPrioritySummary({
    coachSummary,
    generatedAt
  });

  return {
    analysisVersion: UNIFIED_ACTIVITY_ANALYSIS_VERSION,
    input,
    rideClassification,
    enduranceExecution: endurance,
    respiratoryAnalysis: respiratory,
    coachSummary,
    coachPriority,
    rideSourceAgreement: input.supportingRideSources.map(item => item.agreement),
    activityCompletenessPercent: input.dataCoverage.activityCompletenessPercent,
    analysisConfidence: {
      overallPercent,
      rideClassificationPercent: rideClassification.confidencePercent,
      enduranceExecutionPercent: endurance?.confidencePercent ?? null,
      respiratoryAnalysisPercent: respiratory?.confidencePercent ?? null,
      hydrationAnalysisPercent: null
    },
    sessionOverview: {
      sessionType: rideClassification.displayName,
      execution: rideClassification.executionStyle,
      primaryAdaptation: rideClassification.primaryStimulus,
      secondaryAdaptation: rideClassification.secondaryStimulus,
      respiratoryData: respiratory
        ? `${respiratory.aligned ? "Tymewear aligned" : "Tymewear partial"}, ${respiratory.dataCoveragePercent}% coverage`
        : "Not available",
      hydrationData: hydrationPresent ? "Source retained; structured values not available" : "Not available",
      overallAnalysisConfidencePercent: overallPercent
    },
    warnings: [...input.warnings]
  };
}